// EscalaVeiculos.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../services/axios';
import {
  Box,
  TextField,
  Grid,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { format } from 'date-fns';

function EscalaVeiculos() {
  // Estados para filtros e dados
  const [dtInicial, setDtInicial] = useState('2025-02-11');
  const [dtFinal, setDtFinal] = useState('2025-02-25');
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [nomeVeiculo, setNomeVeiculo] = useState('');
  const [fontSize, setFontSize] = useState(14);
  const [escalas, setEscalas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Busca as escalas na API
  const fetchEscalas = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/agenda/listarEscalas', {
        params: {
          emMatrizFilial: 'starline', // ajuste conforme sua lógica
          dtInicial,
          dtFinal,
          idCliente: 0,
          idAgenda: 0,
          nomeCategoria,
          nomeVeiculo,
        },
      });
      setEscalas(response.data);
    } catch (err) {
      console.error('Erro ao buscar escalas:', err);
      setError('Erro ao carregar as escalas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEscalas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gera um array com todas as datas (objetos Date) no intervalo selecionado (inclusive)
  const getDatesInRange = (start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    for (let dt = new Date(startDate); dt <= endDate; dt.setDate(dt.getDate() + 1)) {
      dates.push(new Date(dt));
    }
    return dates;
  };

  const datesRange = getDatesInRange(dtInicial, dtFinal);

  // Agrupa as escalas por veículo (usando o prefixo como chave)
  const groupedEscalas = escalas.reduce((acc, agenda) => {
    const prefixo = agenda.veiculo?.prefixo || 'Sem Veículo';
    if (!acc[prefixo]) {
      acc[prefixo] = { veiculo: agenda.veiculo, agendas: [] };
    }
    acc[prefixo].agendas.push(agenda);
    return acc;
  }, {});

  // Para ordenação: determina o "horário efetivo" para a agenda no dia targetDate.
  // Se a agenda começou antes do período filtrado e o dia não for o dia de chegada, retorna um valor fixo ("000000")
  const getEffectiveTime = (agenda, targetDate) => {
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const partida = new Date(agenda.dataPartida);
    partida.setHours(0, 0, 0, 0);
    const chegada = new Date(agenda.dataChegada);
    chegada.setHours(0, 0, 0, 0);
    const filtroInicio = new Date(dtInicial);
    filtroInicio.setHours(0, 0, 0, 0);

    if (partida < filtroInicio && target.getTime() !== chegada.getTime()) {
      return "000000";
    }
    if (target.getTime() === partida.getTime()) {
      return agenda.horaPartida || '999999';
    }
    if (target.getTime() === chegada.getTime()) {
      return agenda.horaChegada || '999999';
    }
    return '999999';
  };

  // Retorna o texto a ser exibido na célula para o dia targetDate.
  // Se a agenda iniciou antes do período filtrado (dataPartida < dtInicial), então
  // para todos os dias (exceto o dia de chegada) exibe "X".
  // Caso contrário, se o dia for de partida, exibe "Saída: HH:mm";
  // se for o dia de chegada, exibe "Chegada: HH:mm";
  // e nos dias intermediários exibe "Em viagem".
  const getDisplayLabel = (agenda, targetDate) => {
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const partida = new Date(agenda.dataPartida);
    partida.setHours(0, 0, 0, 0);
    const chegada = new Date(agenda.dataChegada);
    chegada.setHours(0, 0, 0, 0);
    const filtroInicio = new Date(dtInicial);
    filtroInicio.setHours(0, 0, 0, 0);

    // Se a viagem iniciou antes do período filtrado, mostra "X" para todos os dias, exceto o dia de chegada
    if (partida < filtroInicio && target.getTime() !== chegada.getTime()) {
      return "X";
    }
    if (target.getTime() === chegada.getTime()) {
      return `Chegada: ${formatTime(agenda.horaChegada)}`;
    }
    if (target.getTime() === partida.getTime()) {
      return `Saída: ${formatTime(agenda.horaPartida)}`;
    }
    return "Em viagem";
  };

  // Formata um horário (string "HHmmss") para "HH:mm"
  const formatTime = (timeStr) => {
    if (!timeStr || timeStr.length < 4) return '';
    return `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`;
  };

  // Define a cor do texto com base na agenda
  const getStatusColor = (agenda) => {
    if (agenda.contrato) return theme.palette.primary.main; // Contrato assinado: cor primária
    if (agenda.status === 'R') return 'green'; // Agenda Reservada
    if (agenda.status === 'C') return 'black'; // Agenda Confirmada
    return 'gray';
  };

  // Manipulação da paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Botões do rodapé
  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 2 }, position: 'relative', fontSize: `${fontSize}px` }}>
      {/* Cabeçalho */}
      <Typography variant="h4" align="center" sx={{ mb: 3 }}>
        Escala de Veículos
      </Typography>

      {/* Filtros */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Data Inicial"
            type="date"
            fullWidth
            value={dtInicial}
            onChange={(e) => setDtInicial(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Data Final"
            type="date"
            fullWidth
            value={dtFinal}
            onChange={(e) => setDtFinal(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Categoria"
            variant="outlined"
            fullWidth
            value={nomeCategoria}
            onChange={(e) => setNomeCategoria(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Veículo"
            variant="outlined"
            fullWidth
            value={nomeVeiculo}
            onChange={(e) => setNomeVeiculo(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Tamanho da Fonte"
            type="number"
            variant="outlined"
            fullWidth
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Button variant="contained" color="primary" onClick={fetchEscalas}>
            Localizar
          </Button>
        </Grid>
      </Grid>

      {/* Legenda de Cores */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
        <Typography variant="caption">
          <Box component="span" sx={{ display: 'inline-block', width: 15, height: 15, bgcolor: 'green', mr: 0.5 }} />
          Agenda Reservada
        </Typography>
        <Typography variant="caption">
          <Box component="span" sx={{ display: 'inline-block', width: 15, height: 15, bgcolor: 'red', mr: 0.5 }} />
          Reserva Vencida
        </Typography>
        <Typography variant="caption">
          <Box component="span" sx={{ display: 'inline-block', width: 15, height: 15, bgcolor: 'black', mr: 0.5 }} />
          Agenda Confirmada
        </Typography>
        <Typography variant="caption">
          <Box component="span" sx={{ display: 'inline-block', width: 15, height: 15, bgcolor: 'orange', mr: 0.5 }} />
          Retorno Vencido
        </Typography>
        <Typography variant="caption">
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 15,
              height: 15,
              bgcolor: theme.palette.primary.main,
              mr: 0.5,
            }}
          />
          Contrato Assinado
        </Typography>
      </Box>

      {/* Tabela de Escalas */}
      {loading ? (
        <Typography align="center">Carregando escalas...</Typography>
      ) : error ? (
        <Typography color="error" align="center">
          {error}
        </Typography>
      ) : (
        <Paper>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }}>VEÍCULO</TableCell>
                  {datesRange.map((date, idx) => (
                    <TableCell key={idx} sx={{ fontWeight: 'bold' }} align="center">
                      {format(date, 'dd/MM')}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(groupedEscalas)
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((prefixo) => {
                    const { veiculo, agendas } = groupedEscalas[prefixo];
                    return (
                      <TableRow key={prefixo}>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          {veiculo?.prefixo || 'Sem Veículo'} {veiculo?.placa ? `(${veiculo.placa})` : ''}
                        </TableCell>
                        {datesRange.map((date, idx) => {
                          // Filtra as agendas que abrangem o dia target
                          const agendasForDay = agendas.filter((agenda) => {
                            const target = new Date(date);
                            target.setHours(0, 0, 0, 0);
                            const partida = new Date(agenda.dataPartida);
                            partida.setHours(0, 0, 0, 0);
                            const chegada = new Date(agenda.dataChegada);
                            chegada.setHours(0, 0, 0, 0);
                            return target >= partida && target <= chegada;
                          });
                          // Ordena as agendas de acordo com o horário efetivo no dia
                          const sortedAgendas = agendasForDay.sort((a, b) => {
                            const timeA = getEffectiveTime(a, date);
                            const timeB = getEffectiveTime(b, date);
                            return timeA.localeCompare(timeB);
                          });
                          return (
                            <TableCell key={idx} align="center">
                              {sortedAgendas.length > 0 ? (
                                sortedAgendas.map((agenda, i) => (
                                  <Box key={i} sx={{ mb: 0.5, color: getStatusColor(agenda) }}>
                                    {agenda.contrato ? (
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                        {agenda.contrato.codigoContrato}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2">
                                        {getDisplayLabel(agenda, date)}
                                      </Typography>
                                    )}
                                  </Box>
                                ))
                              ) : (
                                <Typography variant="caption" sx={{ color: 'gray' }}>
                                  X
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          {Object.keys(groupedEscalas).length > rowsPerPage && (
            <TablePagination
              component="div"
              count={Object.keys(groupedEscalas).length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          )}
        </Paper>
      )}

      {/* Rodapé */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button variant="outlined" color="primary" onClick={handlePrint}>
          Imprimir
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      {/* Logotipo em fundo */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 0.05,
          fontSize: '10rem',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        Starline
      </Box>
    </Box>
  );
}

export default EscalaVeiculos;
